import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getProfile,
  updateProfile,
  getMyMatches,
  getAvailableMatches,
  createMatch,
  updateMatchStatus,
  getMyDonations,
  getOpenSponsorships,
  joinMatch
} from '../../services/communityKitchenService';
import './CommunityKitchen.css';

const STATUS_LABELS = {
  pending_cook: 'Awaiting Cook',
  pending_donor: 'Awaiting Funding',
  funded: 'Funded',
  shopping: 'Shopping',
  cooking: 'Cooking',
  delivering: 'Delivering',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const STATUS_COLORS = {
  pending_cook: '#f59e0b',
  pending_donor: '#f59e0b',
  funded: '#10b981',
  shopping: '#3b82f6',
  cooking: '#8b5cf6',
  delivering: '#06b6d4',
  completed: '#22c55e',
  cancelled: '#ef4444'
};

export default function CommunityKitchenDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('matches');
  const [matches, setMatches] = useState([]);
  const [available, setAvailable] = useState([]);
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchRole, setSearchRole] = useState('');
  const [searchRadius, setSearchRadius] = useState(10);
  const [openSponsorships, setOpenSponsorships] = useState([]);

  const loadProfile = useCallback(async () => {
    try {
      const p = await getProfile();
      if (!p) {
        navigate('/community-kitchen/onboarding');
        return;
      }
      setProfile(p);
      setSearchRadius(p.preferences?.radiusMiles || 10);

      // Set default search role based on user's roles
      if (p.roles.includes('cook')) setSearchRole('recipient');
      else if (p.roles.includes('donor')) setSearchRole('cook');
      else if (p.roles.includes('recipient')) setSearchRole('cook');
    } catch (err) {
      toast.error('Failed to load profile');
    }
  }, [navigate]);

  const loadMatches = useCallback(async () => {
    try {
      const m = await getMyMatches();
      setMatches(m);
    } catch (err) {
      console.error('Failed to load matches:', err);
    }
  }, []);

  const loadDonations = useCallback(async () => {
    try {
      const d = await getMyDonations();
      setDonations(d);
    } catch (err) {
      console.error('Failed to load donations:', err);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadProfile();
      await loadMatches();
      setLoading(false);
    };
    init();
  }, [loadProfile, loadMatches]);

  useEffect(() => {
    if (activeTab === 'donations' && profile?.roles.includes('donor')) {
      loadDonations();
    }
    if (activeTab === 'find' && profile?.roles.includes('recipient')) {
      handleLoadSponsorships();
    }
  }, [activeTab, profile, loadDonations]);

  const handleSearch = async () => {
    if (!searchRole) return;
    try {
      const results = await getAvailableMatches(searchRole, searchRadius);
      setAvailable(results);
    } catch (err) {
      toast.error('Failed to search: ' + err.message);
    }
  };

  const handleClaimRecipient = async (recipientId) => {
    try {
      await createMatch({ recipientId });
      toast.success('Match created! The recipient will be notified.');
      await loadMatches();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRequestCook = async (cookId) => {
    try {
      await createMatch({ cookId });
      toast.success('Request sent! The cook will be notified.');
      await loadMatches();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDonorConnect = async (person) => {
    try {
      const data = person.roles.includes('recipient')
        ? { recipientId: person._id }
        : { cookId: person._id };
      await createMatch(data);
      toast.success('Match created! You can fund it from the match detail page.');
      await loadMatches();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStatusChange = async (matchId, newStatus) => {
    try {
      await updateMatchStatus(matchId, newStatus);
      toast.success(`Match updated to: ${STATUS_LABELS[newStatus]}`);
      await loadMatches();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = profile.status === 'active' ? 'paused' : 'active';
    try {
      const updated = await updateProfile({ status: newStatus });
      setProfile(updated);
      toast.success(`Profile ${newStatus === 'active' ? 'activated' : 'paused'}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLoadSponsorships = async () => {
    try {
      const results = await getOpenSponsorships(searchRadius);
      setOpenSponsorships(results);
    } catch (err) {
      console.error('Failed to load open sponsorships:', err);
    }
  };

  const handleJoinSponsorship = async (matchId) => {
    try {
      await joinMatch(matchId);
      toast.success('You\'ve joined this sponsored match!');
      await loadMatches();
      setActiveTab('matches');
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return (
      <div className="ck-dashboard">
        <div className="ck-loading">Loading Community Kitchen...</div>
      </div>
    );
  }

  if (!profile) return null;

  const activeMatches = matches.filter(m => !['completed', 'cancelled'].includes(m.status));
  const pastMatches = matches.filter(m => ['completed', 'cancelled'].includes(m.status));

  return (
    <div className="ck-dashboard">
      <div className="ck-dashboard-header">
        <div className="ck-dashboard-title">
          <h1>Community Kitchen</h1>
          <div className="ck-profile-summary">
            <span className="ck-display-name">{profile.displayName}</span>
            <div className="ck-role-badges">
              {profile.roles.map(r => (
                <span key={r} className={`ck-badge ck-badge-${r}`}>
                  {r === 'recipient' ? '🍽️' : r === 'cook' ? '👨‍🍳' : '💝'} {r}
                </span>
              ))}
            </div>
            <span className={`ck-status-dot ${profile.status}`} />
            <button className="ck-btn-link" onClick={handleToggleStatus}>
              {profile.status === 'active' ? 'Pause' : 'Activate'}
            </button>
          </div>
        </div>
      </div>

      <div className="ck-tabs">
        <button
          className={`ck-tab ${activeTab === 'matches' ? 'active' : ''}`}
          onClick={() => setActiveTab('matches')}
        >
          My Matches {activeMatches.length > 0 && <span className="ck-tab-count">{activeMatches.length}</span>}
        </button>
        <button
          className={`ck-tab ${activeTab === 'find' ? 'active' : ''}`}
          onClick={() => setActiveTab('find')}
        >
          Find Matches
        </button>
        {profile.roles.includes('donor') && (
          <button
            className={`ck-tab ${activeTab === 'donations' ? 'active' : ''}`}
            onClick={() => setActiveTab('donations')}
          >
            Donations
          </button>
        )}
        <button
          className={`ck-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          My Profile
        </button>
      </div>

      {/* Role-specific how-it-works guide */}
      {activeTab === 'matches' && activeMatches.length === 0 && (
        <div className="ck-how-it-works">
          <h3>How Community Kitchen Works</h3>
          <div className="ck-how-steps">
            {profile.roles.includes('recipient') && (
              <div className="ck-how-card">
                <span className="ck-how-icon">🍽️</span>
                <strong>As a Recipient</strong>
                <ol className="ck-how-list">
                  <li>Go to <em>Find Matches</em> and request a cook near you</li>
                  <li>A donor funds the grocery cost</li>
                  <li>The cook orders ingredients via Instacart, prepares your meal, and delivers it</li>
                </ol>
              </div>
            )}
            {profile.roles.includes('cook') && (
              <div className="ck-how-card">
                <span className="ck-how-icon">👨‍🍳</span>
                <strong>As a Cook</strong>
                <ol className="ck-how-list">
                  <li>Find a recipient who needs a meal, or wait for a request</li>
                  <li>Once a donor funds the match, select recipes and order groceries via Instacart</li>
                  <li>Prepare the meal and deliver or arrange pickup</li>
                </ol>
              </div>
            )}
            {profile.roles.includes('donor') && (
              <div className="ck-how-card">
                <span className="ck-how-icon">💝</span>
                <strong>As a Donor</strong>
                <ol className="ck-how-list">
                  <li>Find a cook or recipient to sponsor, or browse funded matches</li>
                  <li>Fund the grocery cost via secure Stripe checkout</li>
                  <li>Groceries are ordered on Instacart — 100% of your donation goes to ingredients</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="ck-tab-content">
        {activeTab === 'matches' && (
          <div className="ck-matches-tab">
            {activeMatches.length === 0 && pastMatches.length === 0 ? (
              <div className="ck-empty-state">
                <h3>No matches yet</h3>
                <p>Start by finding people in your area to connect with.</p>
                <button className="ck-btn ck-btn-primary" onClick={() => setActiveTab('find')}>
                  Find Matches
                </button>
              </div>
            ) : (
              <>
                {activeMatches.length > 0 && (
                  <div className="ck-section">
                    <h3>Active Matches</h3>
                    <div className="ck-match-list">
                      {activeMatches.map(match => (
                        <MatchCard
                          key={match._id}
                          match={match}
                          profile={profile}
                          onStatusChange={handleStatusChange}
                          onNavigate={() => navigate(`/community-kitchen/match/${match._id}`)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {pastMatches.length > 0 && (
                  <div className="ck-section">
                    <h3>Past Matches</h3>
                    <div className="ck-match-list">
                      {pastMatches.map(match => (
                        <MatchCard
                          key={match._id}
                          match={match}
                          profile={profile}
                          onNavigate={() => navigate(`/community-kitchen/match/${match._id}`)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'find' && (
          <div className="ck-find-tab">
            <div className="ck-search-controls">
              <div className="ck-field-group">
                <label className="ck-label">Looking for</label>
                <select
                  className="ck-select"
                  value={searchRole}
                  onChange={e => setSearchRole(e.target.value)}
                >
                  <option value="">Select role...</option>
                  {profile.roles.includes('cook') && <option value="recipient">Recipients who need meals</option>}
                  {profile.roles.includes('recipient') && <option value="cook">Cooks who can help</option>}
                  {profile.roles.includes('donor') && <option value="cook">Cooks to fund</option>}
                  {profile.roles.includes('donor') && <option value="recipient">Recipients to help</option>}
                </select>
              </div>

              <div className="ck-field-group">
                <label className="ck-label">Within {searchRadius} miles</label>
                <input
                  type="range"
                  className="ck-range"
                  min={1}
                  max={50}
                  value={searchRadius}
                  onChange={e => setSearchRadius(parseInt(e.target.value))}
                />
              </div>

              <button
                className="ck-btn ck-btn-primary"
                onClick={handleSearch}
                disabled={!searchRole}
              >
                Search
              </button>
            </div>

            {available.length > 0 ? (
              <div className="ck-available-list">
                {available.map(person => (
                  <div key={person._id} className="ck-available-card">
                    <div className="ck-available-info">
                      <strong>{person.displayName}</strong>
                      <span className="ck-location-text">
                        {person.location?.city}, {person.location?.state}
                      </span>
                      {person.bio && <p className="ck-bio-preview">{person.bio}</p>}
                      <div className="ck-tag-row">
                        {person.preferences?.dietaryRestrictions?.map(d => (
                          <span key={d} className="ck-tag">{d}</span>
                        ))}
                        {person.cookDetails?.cuisineSpecialties?.map(c => (
                          <span key={c} className="ck-tag ck-tag-cuisine">{c}</span>
                        ))}
                      </div>
                      <div className="ck-tag-row">
                        {person.preferences?.logisticsPreference?.map(l => (
                          <span key={l} className="ck-tag ck-tag-logistics">{l}</span>
                        ))}
                        {person.preferences?.availableDays?.map(d => (
                          <span key={d} className="ck-tag ck-tag-day">{d.slice(0, 3)}</span>
                        ))}
                      </div>
                    </div>
                    <div className="ck-available-actions">
                      {profile.roles.includes('cook') && person.roles?.includes('recipient') && (
                        <button
                          className="ck-btn ck-btn-primary ck-btn-sm"
                          onClick={() => handleClaimRecipient(person._id)}
                        >
                          👨‍🍳 Offer to Cook
                        </button>
                      )}
                      {profile.roles.includes('recipient') && person.roles?.includes('cook') && (
                        <button
                          className="ck-btn ck-btn-primary ck-btn-sm"
                          onClick={() => handleRequestCook(person._id)}
                        >
                          🍽️ Request This Cook
                        </button>
                      )}
                      {profile.roles.includes('donor') && (
                        <button
                          className="ck-btn ck-btn-primary ck-btn-sm"
                          onClick={() => handleDonorConnect(person)}
                        >
                          💝 Sponsor
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ck-empty-state">
                <p>Use the search above to find people in your area.</p>
              </div>
            )}

            {/* Open Sponsorships for Recipients */}
            {profile.roles.includes('recipient') && openSponsorships.length > 0 && (
              <div className="ck-section" style={{ marginTop: '2rem' }}>
                <h3>💝 Sponsored Meals Available</h3>
                <p className="ck-step-desc">These meals are already funded by donors and have a cook ready — they just need a recipient!</p>
                <div className="ck-available-list">
                  {openSponsorships.map(match => (
                    <div key={match._id} className="ck-available-card">
                      <div className="ck-available-info">
                        <strong>👨‍🍳 {match.cookId?.displayName || 'Volunteer Cook'}</strong>
                        <span className="ck-location-text">
                          {match.cookId?.location?.city}, {match.cookId?.location?.state}
                        </span>
                        {match.cookId?.bio && <p className="ck-bio-preview">{match.cookId.bio}</p>}
                        {match.cookId?.cookDetails?.cuisineSpecialties?.length > 0 && (
                          <div className="ck-tag-row">
                            {match.cookId.cookDetails.cuisineSpecialties.map(c => (
                              <span key={c} className="ck-tag ck-tag-cuisine">{c}</span>
                            ))}
                          </div>
                        )}
                        <span className="ck-hint">💝 Sponsored by {match.donorId?.displayName || 'a donor'}</span>
                      </div>
                      <div className="ck-available-actions">
                        <button
                          className="ck-btn ck-btn-primary ck-btn-sm"
                          onClick={() => handleJoinSponsorship(match._id)}
                        >
                          🍽️ Accept This Meal
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'donations' && (
          <div className="ck-donations-tab">
            {donations.length === 0 ? (
              <div className="ck-empty-state">
                <h3>No donations yet</h3>
                <p>Find a match to fund, or browse cooks in your area.</p>
              </div>
            ) : (
              <div className="ck-donation-list">
                {donations.map(d => (
                  <div key={d._id} className="ck-donation-card">
                    <div className="ck-donation-amount">${(d.amount / 100).toFixed(2)}</div>
                    <div className="ck-donation-info">
                      <span className="ck-donation-type">{d.type.replace('_', ' ')}</span>
                      <span className={`ck-status-badge ${d.status}`}>{d.status}</span>
                    </div>
                    <div className="ck-donation-date">
                      {new Date(d.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="ck-profile-tab">
            <div className="ck-profile-card">
              <h3>{profile.displayName}</h3>
              {profile.bio && <p className="ck-bio">{profile.bio}</p>}
              <div className="ck-profile-detail">
                <strong>Location:</strong> {profile.location?.city}, {profile.location?.state} {profile.location?.zip}
              </div>
              <div className="ck-profile-detail">
                <strong>Radius:</strong> {profile.preferences?.radiusMiles} miles
              </div>
              <div className="ck-profile-detail">
                <strong>Logistics:</strong> {profile.preferences?.logisticsPreference?.join(', ')}
              </div>
              {profile.preferences?.dietaryRestrictions?.length > 0 && (
                <div className="ck-profile-detail">
                  <strong>Dietary:</strong> {profile.preferences.dietaryRestrictions.join(', ')}
                </div>
              )}
              {profile.preferences?.availableDays?.length > 0 && (
                <div className="ck-profile-detail">
                  <strong>Available:</strong> {profile.preferences.availableDays.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ')}
                </div>
              )}

              {profile.roles.includes('cook') && profile.cookDetails && (
                <div className="ck-profile-section">
                  <h4>Cook Details</h4>
                  <p>Max meals/week: {profile.cookDetails.maxMealsPerWeek}</p>
                  {profile.cookDetails.cuisineSpecialties?.length > 0 && (
                    <p>Specialties: {profile.cookDetails.cuisineSpecialties.join(', ')}</p>
                  )}
                  <p>Transportation: {profile.cookDetails.hasTransportation ? 'Yes' : 'No'}</p>
                  <p>Grocery ordering: {
                    profile.cookDetails.groceryOrderPreference === 'cook_orders' ? 'I order groceries' :
                    profile.cookDetails.groceryOrderPreference === 'donor_orders' ? 'Donor orders groceries' :
                    'Flexible'
                  }</p>
                </div>
              )}

              {profile.roles.includes('donor') && profile.donorDetails && (
                <div className="ck-profile-section">
                  <h4>Donor Details</h4>
                  <p>Budget/meal: ${(profile.donorDetails.budgetPerMeal / 100).toFixed(2)}</p>
                  <p>Max donations/month: {profile.donorDetails.maxDonationsPerMonth}</p>
                  <p>Grocery ordering: {
                    profile.donorDetails.groceryOrderPreference === 'donor_orders' ? 'I order groceries' :
                    profile.donorDetails.groceryOrderPreference === 'cook_orders' ? 'Cook orders groceries' :
                    'Flexible'
                  }</p>
                </div>
              )}

              <button
                className="ck-btn ck-btn-secondary"
                onClick={() => navigate('/community-kitchen/onboarding')}
                style={{ marginTop: '1rem' }}
              >
                Edit Profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

const NEXT_STEP_HINTS = {
  recipient: {
    pending_recipient: 'Waiting for you to confirm this match — click to review and accept.',
    pending_cook: 'A cook is reviewing your request. Sit tight!',
    pending_donor: 'Cook confirmed! Now waiting for a donor to fund the groceries.',
    funded: 'Groceries are funded! The cook will order ingredients soon.',
    shopping: 'The cook is shopping for your meal ingredients.',
    cooking: 'Your meal is being prepared right now!',
    delivering: 'Your meal is on the way!',
    completed: 'Meal delivered! Consider sharing a thank-you or photo.',
    cancelled: 'This match was cancelled.'
  },
  cook: {
    pending_cook: 'A recipient needs your help — click to review and accept this match.',
    pending_recipient: 'Looking for a recipient to join this match.',
    pending_donor: 'Waiting for a donor to fund the groceries. You can share this match to attract sponsors.',
    funded: 'Groceries are funded! Select recipes, then order ingredients via Instacart.',
    shopping: 'Ingredients ordered — complete your Instacart checkout if needed, then start cooking!',
    cooking: 'You\'re cooking! When ready, mark the delivery as started.',
    delivering: 'Deliver the meal and mark as completed when done.',
    completed: 'Great work! Consider uploading a photo of the finished meal.',
    cancelled: 'This match was cancelled.'
  },
  donor: {
    pending_recipient: 'Looking for a recipient for this match.',
    pending_cook: 'Waiting for a cook to join this match.',
    pending_donor: 'This match needs funding — click to sponsor the groceries!',
    funded: 'You funded this meal! The cook will order groceries next.',
    shopping: 'Groceries are being ordered via Instacart.',
    cooking: 'The cook is preparing the meal you sponsored.',
    delivering: 'The meal you sponsored is being delivered!',
    completed: 'Meal delivered! Your sponsorship made a difference.',
    cancelled: 'This match was cancelled.'
  }
};

function MatchCard({ match, profile, onStatusChange, onNavigate }) {
  const myRole = match.cookId?._id === profile._id ? 'cook'
    : match.recipientId?._id === profile._id ? 'recipient'
    : match.donorId?._id === profile._id ? 'donor'
    : 'donor';

  const otherPerson = myRole === 'cook'
    ? match.recipientId
    : myRole === 'recipient'
    ? match.cookId
    : match.cookId;

  const nextStatus = {
    pending_cook: myRole === 'recipient' ? null : 'pending_donor',
    pending_donor: null,
    funded: myRole === 'cook' ? 'shopping' : null,
    shopping: myRole === 'cook' ? 'cooking' : null,
    cooking: myRole === 'cook' ? 'delivering' : null,
    delivering: myRole === 'cook' ? 'completed' : null
  };

  const nextAction = nextStatus[match.status];

  return (
    <div className="ck-match-card" onClick={onNavigate}>
      <div className="ck-match-card-header">
        <span className="ck-match-name">{otherPerson?.displayName || 'Unknown'}</span>
        <span
          className="ck-status-badge"
          style={{ backgroundColor: STATUS_COLORS[match.status], color: '#fff' }}
        >
          {STATUS_LABELS[match.status]}
        </span>
      </div>

      <div className="ck-match-card-body">
        <span className="ck-match-role">You: {myRole}</span>
        {otherPerson?.location && (
          <span className="ck-location-text">
            {otherPerson.location.city}, {otherPerson.location.state}
          </span>
        )}
        {match.scheduledDate && (
          <span className="ck-match-date">
            {new Date(match.scheduledDate).toLocaleDateString()}
          </span>
        )}
        {NEXT_STEP_HINTS[myRole]?.[match.status] && (
          <p className="ck-next-step-hint">
            💡 {NEXT_STEP_HINTS[myRole][match.status]}
          </p>
        )}
      </div>

      {nextAction && onStatusChange && (
        <div className="ck-match-card-actions" onClick={e => e.stopPropagation()}>
          <button
            className="ck-btn ck-btn-primary ck-btn-sm"
            onClick={() => onStatusChange(match._id, nextAction)}
          >
            {nextAction === 'pending_donor' ? 'Accept & Find Donor' :
             nextAction === 'shopping' ? 'Start Shopping' :
             nextAction === 'cooking' ? 'Start Cooking' :
             nextAction === 'delivering' ? 'Out for Delivery' :
             nextAction === 'completed' ? 'Mark Delivered' : nextAction}
          </button>
          <button
            className="ck-btn ck-btn-danger ck-btn-sm"
            onClick={() => onStatusChange(match._id, 'cancelled')}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
